import { type DataContainer } from "@otto-ec/tracking-bct";
import { SanChatBotResponseLabels, TrackingBackendKeys, type TrackingLabels } from "../types/Websocket";
import type { ChatMessage } from "../types/PersistentState";
import { tracking } from "../lib/Tracking";
import { toggle } from "@otto-ec/global-resources/toggle";
import { getIsVoiceMode } from "./voice-mode.svelte";
import { voiceTracker } from "./voice-tracking";

export const chatTurnTrackingStack: { data: DataContainer } = { data: {} };
const BASE_TRACKING_DATA: DataContainer = {
    "wk.san_ActiveSearch": ["true"],
    "wk.san_SearchType": ["chatbot"],
};

/**
 *
 *
 *
 *
 */
export function normalizeSearchTerm(searchTerm: string): string {
    return searchTerm
        .trim()
        .toLowerCase()
        .replace(/[[\]{}"]/g, "");
}

/**
 *
 *
 *
 *
 */
export function setDialogueIdTracking(dialogueId: string, mergeToPageImpression: boolean = false) {
    const isDialogueIdTrackingEnabled = toggle.get("nucleus_activate_dialogue_id_tracking", false);
    if (!isDialogueIdTrackingEnabled) {
        return;
    }

    const trackingData = { "wk.san_ChatBotDialogueId": [dialogueId] };

    if (mergeToPageImpression) {
        tracking.submitMerge(trackingData);
    } else {
        tracking.trackSubmitEvent(trackingData);
    }
}

export function trackPDPClick(
    sourceLabel: "link_pdp_cinema" | "link_pdp_advisory" | "link_pdp_advisory_comparison" | "link_pdp_serp",
    productLinkType: string,
    backendLabels?: TrackingLabels,
    searchQuery?: string,
    tileIndex?: number,
    duration?: number,
    productsCount?: number,
) {
    const trackingData: DataContainer = getBaseTrackingData(backendLabels, searchQuery);
    trackingData["san_ChatBotInteraction"] = [sourceLabel];
    trackingData["wk.san_ChatBotAttribution"] = [sourceLabel];
    trackingData["wk.san_ProductType"] = ["organic"];
    trackingData["wk.san_ProductLinkType"] = [productLinkType];
    if (tileIndex !== undefined) {
        trackingData["wk.san_ListPosition"] = [tileIndex.toString()];
    }
    trackingData["wk.san_ProductClickListType"] = ["chatbot"];
    trackingData["san_Interaction"] = ["chatbot_product_tile"];
    if (productsCount !== undefined) {
        trackingData["san_ChatBotVoiceProductsCount"] = [productsCount.toString()];
    }
    if (duration !== undefined) {
        trackingData["san_ChatBotVoiceDuration"] = [duration.toString()];
    }
    tracking.trackOnNextPageImpression(trackingData);
}

export function trackSliderInteraction() {
    tracking.trackSubmitEvent({ san_ChatBotInteraction: ["cinema_next"] });
}

export function trackFloatingActionButtonClick() {
    tracking.trackOnNextPageImpression({ san_ChatBotInteraction: ["actionButton_open"], "wk.san_ChatBotEntry": ["actionButton"] });
}
export function trackBannerClick() {
    tracking.trackOnNextPageImpression({ san_ChatBotInteraction: ["banner_open"], "wk.san_ChatBotEntry": ["banner"] });
}

export function trackProductListClick(backendLabels?: TrackingLabels, searchQuery?: string) {
    const trackingData: DataContainer = getBaseTrackingData(backendLabels, searchQuery);

    trackingData["san_ChatBotInteraction"] = ["link_list"];
    trackingData["wk.san_ChatBotAttribution"] = ["link_list"];
    trackingData["san_Interaction"] = ["chatbot_list"];

    tracking.trackOnNextPageImpression(trackingData);
}

function getBaseTrackingData(backendLabels?: TrackingLabels, searchQuery?: string): DataContainer {
    const trackingData: DataContainer = { ...BASE_TRACKING_DATA };
    if (backendLabels) {
        for (const [key, value] of Object.entries(backendLabels)) {
            if (Object.values(TrackingBackendKeys).includes(key as TrackingBackendKeys)) {
                trackingData[key] = [value]; /*                          */
            }
        }
    }
    if (searchQuery) {
        const normalizedSearchQuery = normalizeSearchTerm(searchQuery);
        trackingData["wk.san_SearchTerm"] = [normalizedSearchQuery];
        trackingData["wk.san_SearchWordCount"] = [
            normalizedSearchQuery
                .split(" ")
                .filter((word) => word.length > 0)
                .length.toString(10),
        ];
    }
    return trackingData;
}

export function trackRestart() {
    tracking.trackSubmitEvent({ san_ChatBotInteraction: ["restart"] });
}

export function trackClose(duration?: number, productsCount?: number) {
    tracking.trackOnNextPageImpression({
        san_ChatBotInteraction: ["close"],
        "wk.san_ChatBotAttribution": ["close"],
        ...(duration ? { san_ChatBotVoiceDuration: [duration.toString()] } : {}),
        ...(productsCount ? { san_ChatBotVoiceProductsCount: [productsCount.toString()] } : {}),
    });
}

export function trackVoiceStart() {
    tracking.trackSubmitEvent({ san_ChatBotInteraction: ["voice_start"] });
}

export function trackVoiceClose(duration: number, productsCount?: number) {
    tracking.trackSubmitEvent({
        san_ChatBotInteraction: ["voice_close"],
        san_ChatBotVoiceDuration: [duration.toString()],
        ...(productsCount ? { san_ChatBotVoiceProductsCount: [productsCount.toString()] } : {}),
    });
}

export function trackVoicePause() {
    tracking.trackSubmitEvent({ san_ChatBotInteraction: ["voice_pause"] });
}

export function addStarterPromptToTurn() {
    chatTurnTrackingStack.data["san_ChatBotInteraction"] = ["prompt_starter"];
    chatTurnTrackingStack.data["wk.san_ChatBotStarterPrompt"] = ["true"];
}

export function addUserMessageToTurn() {
    chatTurnTrackingStack.data["san_ChatBotInteraction"] = ["prompt_user"];
}

export function addUserVoiceMessageToTurn() {
    chatTurnTrackingStack.data["san_ChatBotInteraction"] = ["prompt_voice"];
}

export function addQuickResponseMessageToTurn() {
    chatTurnTrackingStack.data["san_ChatBotInteraction"] = ["quick_response"];
}

export function addPromptAccessToTurn() {
    chatTurnTrackingStack.data["san_ChatBotInteraction"] = ["prompt_access"];
}

export function addProductListResponse() {
    if (getIsVoiceMode()) {
        addResponseTypeToTrackingData(SanChatBotResponseLabels.PRODUCTS_SERP);
    } else {
        addResponseTypeToTrackingData(SanChatBotResponseLabels.LINK_LIST);
        addResponseTypeToTrackingData(SanChatBotResponseLabels.PRODUCTS_CINEMA);
    }
}

export function trackTurn(chatMessages: ChatMessage[]) {
    finalizeResponseTracking(chatMessages);
    tracking.trackSubmitEvent(chatTurnTrackingStack.data);
    resetTurnTracking();
}

export function addResponseTypeToTrackingData(type: SanChatBotResponseLabels) {
    const currentResponses = chatTurnTrackingStack.data.san_ChatBotResponse;
    const currentArray = Array.isArray(currentResponses) ? currentResponses : [];
    if (!currentArray.includes(type)) {
        currentArray.push(type);
        chatTurnTrackingStack.data.san_ChatBotResponse = currentArray;
    }
}

export function addQuickResponseTrackingIfPresent(chatMessages: ChatMessage[]) {
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (lastMessage?.quick_response_options && lastMessage.quick_response_options.length > 0) {
        addResponseTypeToTrackingData(SanChatBotResponseLabels.QUICK_RESPONSE);
    }
}

export function addVisibleProductsCountToTrackingData() {
    const visibleProductsCountOld = voiceTracker.getProductsCount();
    if (visibleProductsCountOld !== undefined) {
        chatTurnTrackingStack.data["san_ChatBotVoiceProductsCount"] = [visibleProductsCountOld.toString()];
    }
}

export function finalizeResponseTracking(chatMessages: ChatMessage[]) {
    addQuickResponseTrackingIfPresent(chatMessages);

    const currentResponses = chatTurnTrackingStack.data.san_ChatBotResponse;
    if (!Array.isArray(currentResponses) || currentResponses.length === 0) {
        return;
    }

    const significantResponses = currentResponses.filter((response) => response !== SanChatBotResponseLabels.TEXT && response !== SanChatBotResponseLabels.ERROR);

    /*                                                                              */
    if (significantResponses.length > 0) {
        chatTurnTrackingStack.data.san_ChatBotResponse = significantResponses;
    }
}

export function addProductListInformationToTrackingData(tracking?: TrackingLabels, searchQuery?: string) {
    addProductListResponse();
    if (tracking) {
        for (const [key, value] of Object.entries(tracking)) {
            if (Object.values(TrackingBackendKeys).includes(key as TrackingBackendKeys)) {
                chatTurnTrackingStack.data[key] = [value];
            }
        }
    }

    chatTurnTrackingStack.data["wk.san_ActiveSearch"] = ["true"];
    chatTurnTrackingStack.data["wk.san_SearchType"] = ["chatbot"];
    chatTurnTrackingStack.data["san_InitialSearch"] = ["true"];
    chatTurnTrackingStack.data["san_Interaction"] = ["initial_search"];
    if (searchQuery) {
        const normalizedSearchQuery = normalizeSearchTerm(searchQuery);
        chatTurnTrackingStack.data["wk.san_SearchTerm"] = [normalizedSearchQuery];
        chatTurnTrackingStack.data["wk.san_SearchWordCount"] = [
            normalizedSearchQuery
                .split(" ")
                .filter((word) => word.length > 0)
                .length.toString(10),
        ];
    }
}

export function addDetectedProductBaseClassesToTrackingData(detectedProductBaseClasses?: string[]) {
    if (detectedProductBaseClasses?.length) {
        chatTurnTrackingStack.data["san_ChatBotPbk"] = detectedProductBaseClasses;
    }
}

export function resetTurnTracking() {
    chatTurnTrackingStack.data = {};
}